import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useTranslation } from 'react-i18next';

import { useSalonsListQuery } from '../../../../services/GLOWClient';
import { theme } from '../../../../theme';

const ClientChatSalonSearch = ({ onSelectSalon, onBack }) => {
  const { i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedQuery(query.trim()),
      300,
    );
    return () => window.clearTimeout(handle);
  }, [query]);

  const { data = [], isLoading, error } = useSalonsListQuery(
    debouncedQuery
      ? { search: debouncedQuery, language: i18n.language }
      : { language: i18n.language },
  );

  const salons = useMemo(() => (
    (Array.isArray(data) ? data : data?.results || [])
      .filter((salon) => salon.client_chat_enabled === true)
  ), [data]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          pt: 1.5,
        }}
      >
        <IconButton
          aria-label="Retour aux discussions"
          onClick={onBack}
          sx={{ color: theme.colors.primary[950] }}
        >
          <ArrowBackRoundedIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 800 }}>
          Choisir un salon
        </Typography>
      </Box>
      <Box sx={{ px: 2, py: 1.25 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Rechercher un salon"
          inputProps={{ 'aria-label': 'Rechercher un salon' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon
                  fontSize="small"
                  sx={{ color: theme.colors.primary[800] }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              backgroundColor: 'white',
              '&.Mui-focused fieldset': {
                borderColor: theme.colors.primary[700],
              },
            },
          }}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', flex: 1 }}>
          <CircularProgress
            size={28}
            sx={{ color: theme.colors.primary[700] }}
          />
        </Box>
      ) : (
        <List
          aria-label="Salons disponibles"
          disablePadding
          sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1 }}
        >
          {salons.map((salon) => {
            const name = salon.name || 'Salon';
            return (
              <ListItemButton
                key={salon.salon_id ?? salon.id}
                onClick={() => onSelectSalon(salon)}
                sx={{
                  mb: 0.75,
                  borderRadius: '14px',
                  '&:hover': {
                    backgroundColor: theme.colors.primary[50],
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={
                      salon.profile_photo
                      || salon.logo
                      || salon.photo
                      || undefined
                    }
                    alt=""
                    sx={{
                      backgroundColor: theme.colors.secondary[300],
                      color: theme.colors.secondary[950],
                      fontWeight: 800,
                    }}
                  >
                    {name.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={name}
                  secondary={salon.city?.name || salon.address || undefined}
                  primaryTypographyProps={{
                    noWrap: true,
                    sx: { fontWeight: 700 },
                  }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            );
          })}
        </List>
      )}

      {!isLoading && (error || !salons.length) && (
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {error
              ? 'Impossible de charger les salons.'
              : 'Aucun salon disponible pour les messages.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ClientChatSalonSearch;
